import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Upload, FileText, Image, X, CheckCircle, AlertCircle, Crown } from 'lucide-react';
import { ocrService, OCRResult } from '@/services/ocrService';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface FileUploadSectionProps {
  onQuestionsExtracted: (result: OCRResult) => void;
  subject?: string;
  onSubjectChange?: (subject: string) => void;
}

const FileUploadSection: React.FC<FileUploadSectionProps> = ({
  onQuestionsExtracted,
  subject = '',
  onSubjectChange
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [localSubject, setLocalSubject] = useState(subject);
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { hasAIAccess } = useAuth();

  // Check if user has tier 2 access
  if (!hasAIAccess('advanced')) {
    return (
      <Card data-id="hmfj5udp9" data-path="src/components/questions/FileUploadSection.tsx">
        <CardContent className="pt-6" data-id="zyhuiztsi" data-path="src/components/questions/FileUploadSection.tsx">
          <div className="text-center space-y-4" data-id="b96p1bhgd" data-path="src/components/questions/FileUploadSection.tsx">
            <Crown className="h-12 w-12 mx-auto text-amber-500" data-id="0n5ssehuv" data-path="src/components/questions/FileUploadSection.tsx" />
            <h3 className="text-lg font-semibold" data-id="qzbhdza7j" data-path="src/components/questions/FileUploadSection.tsx">Premium Feature</h3>
            <p className="text-gray-600" data-id="95py5ongt" data-path="src/components/questions/FileUploadSection.tsx">
              Extract questions from files is available for Tier 2 users only. Contact your administrator to upgrade your tier.
            </p>
          </div>
        </CardContent>
      </Card>);

  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const isPDF = file.type === 'application/pdf';
      const isImage = file.type.startsWith('image/');

      if (!isPDF && !isImage) {
        toast({
          title: "Invalid File Type",
          description: "Please upload a PDF file or an image (PNG, JPG, JPEG).",
          variant: "destructive"
        });
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "File size must be less than 10MB.",
          variant: "destructive"
        });
        return;
      }

      setSelectedFile(file);
      setProgress(0);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleProcessFile = async () => {
    if (!selectedFile) {
      toast({
        title: "No File Selected",
        description: "Please select a file to process.",
        variant: "destructive"
      });
      return;
    }

    if (!localSubject.trim()) {
      toast({
        title: "Subject Required",
        description: "Please enter a subject for the questions.",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);
    setProgress(10);

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 90));
      }, 500);

      const result = await ocrService.processFile(selectedFile, localSubject, difficulty);

      clearInterval(progressInterval);
      setProgress(100);

      toast({
        title: "Questions Extracted Successfully",
        description: `Generated ${result.questions.length} questions from ${selectedFile.name}`
      });

      onQuestionsExtracted(result);

      // Reset form
      setSelectedFile(null);
      setProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error) {
      console.error('Error processing file:', error);
      toast({
        title: "Processing Failed",
        description: error instanceof Error ? error.message : "Failed to process file",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const getFileIcon = (file: File) => {
    if (file.type === 'application/pdf') {
      return <FileText className="h-8 w-8 text-red-500" data-id="itg452bqn" data-path="src/components/questions/FileUploadSection.tsx" />;
    }
    return <Image className="h-8 w-8 text-blue-500" data-id="8vqhzyssv" data-path="src/components/questions/FileUploadSection.tsx" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
    return Math.round(bytes / (1024 * 1024)) + ' MB';
  };

  return (
    <Card data-id="onui81rnd" data-path="src/components/questions/FileUploadSection.tsx">
      <CardHeader data-id="kxf0almfc" data-path="src/components/questions/FileUploadSection.tsx">
        <CardTitle className="flex items-center gap-2" data-id="k0p7t6dlr" data-path="src/components/questions/FileUploadSection.tsx">
          <Upload className="h-5 w-5" data-id="akmefhfm7" data-path="src/components/questions/FileUploadSection.tsx" />
          <Crown className="h-4 w-4 text-amber-500" data-id="gtrs0h8qk" data-path="src/components/questions/FileUploadSection.tsx" />
          Extract Questions from PDF/Image
        </CardTitle>
        <CardDescription data-id="5qfgrcqnr" data-path="src/components/questions/FileUploadSection.tsx">
          Upload a PDF document or image containing questions, and AI will extract them automatically. (Tier 2 feature)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4" data-id="m9zmn3wmb" data-path="src/components/questions/FileUploadSection.tsx">
        {/* Subject and Difficulty Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="4inggx7uy" data-path="src/components/questions/FileUploadSection.tsx">
          <div className="space-y-2" data-id="a88ngkiur" data-path="src/components/questions/FileUploadSection.tsx">
            <Label htmlFor="subject" data-id="bj2vdmszc" data-path="src/components/questions/FileUploadSection.tsx">Subject</Label>
            <Input
              id="subject"
              value={localSubject}
              onChange={(e) => {
                setLocalSubject(e.target.value);
                onSubjectChange?.(e.target.value);
              }}
              placeholder="e.g., Mathematics, Science, History"
              disabled={isProcessing} data-id="s8lfjsryq" data-path="src/components/questions/FileUploadSection.tsx" />

          </div>
          <div className="space-y-2" data-id="8rthdefzv" data-path="src/components/questions/FileUploadSection.tsx">
            <Label htmlFor="difficulty" data-id="6uu8d8m1f" data-path="src/components/questions/FileUploadSection.tsx">Difficulty Level</Label>
            <Select
              value={difficulty}
              onValueChange={(value: 'easy' | 'medium' | 'hard') => setDifficulty(value)}
              disabled={isProcessing} data-id="ou1klij6o" data-path="src/components/questions/FileUploadSection.tsx">

              <SelectTrigger data-id="636bpiuou" data-path="src/components/questions/FileUploadSection.tsx">
                <SelectValue data-id="yotipynz0" data-path="src/components/questions/FileUploadSection.tsx" />
              </SelectTrigger>
              <SelectContent data-id="2um15cbii" data-path="src/components/questions/FileUploadSection.tsx">
                <SelectItem value="easy" data-id="uopjtvpnr" data-path="src/components/questions/FileUploadSection.tsx">Easy</SelectItem>
                <SelectItem value="medium" data-id="zc03yq1u1" data-path="src/components/questions/FileUploadSection.tsx">Medium</SelectItem>
                <SelectItem value="hard" data-id="jevuba0ks" data-path="src/components/questions/FileUploadSection.tsx">Hard</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* File Upload Area */}
        <div className="space-y-4" data-id="s2oeh9b3x" data-path="src/components/questions/FileUploadSection.tsx">
          <Label data-id="ocsbzzzsp" data-path="src/components/questions/FileUploadSection.tsx">Upload File</Label>
          
          {!selectedFile ?
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()} data-id="sgbhsvnj0" data-path="src/components/questions/FileUploadSection.tsx">

              <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="y9qkpfjoe" data-path="src/components/questions/FileUploadSection.tsx" />
              <p className="text-lg font-medium text-gray-600 mb-2" data-id="2h5mr5cs8" data-path="src/components/questions/FileUploadSection.tsx">
                Drop files here or click to upload
              </p>
              <p className="text-sm text-gray-500" data-id="mspux0xzt" data-path="src/components/questions/FileUploadSection.tsx">
                Supports PDF files and images (PNG, JPG, JPEG) up to 10MB
              </p>
            </div> :

          <Card data-id="19rtnilyo" data-path="src/components/questions/FileUploadSection.tsx">
              <CardContent className="p-4" data-id="chq5ro7mt" data-path="src/components/questions/FileUploadSection.tsx">
                <div className="flex items-center justify-between" data-id="c65lvc3ja" data-path="src/components/questions/FileUploadSection.tsx">
                  <div className="flex items-center space-x-3" data-id="hf5kme1qa" data-path="src/components/questions/FileUploadSection.tsx">
                    {getFileIcon(selectedFile)}
                    <div data-id="z2b0ppy6j" data-path="src/components/questions/FileUploadSection.tsx">
                      <p className="font-medium" data-id="ypjpsg6wu" data-path="src/components/questions/FileUploadSection.tsx">{selectedFile.name}</p>
                      <p className="text-sm text-gray-500" data-id="uhagb3yf9" data-path="src/components/questions/FileUploadSection.tsx">
                        {formatFileSize(selectedFile.size)}
                      </p>
                    </div>
                  </div>
                  <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveFile}
                  disabled={isProcessing} data-id="i7mup3w3o" data-path="src/components/questions/FileUploadSection.tsx">

                    <X className="h-4 w-4" data-id="onwcuxr9y" data-path="src/components/questions/FileUploadSection.tsx" />
                  </Button>
                </div>
                
                {isProcessing &&
              <div className="mt-4 space-y-2" data-id="d2bjkiwdt" data-path="src/components/questions/FileUploadSection.tsx">
                    <div className="flex justify-between text-sm" data-id="g3r2jgq8k" data-path="src/components/questions/FileUploadSection.tsx">
                      <span data-id="83tgdl9cc" data-path="src/components/questions/FileUploadSection.tsx">Processing...</span>
                      <span data-id="tv0dwc6l4" data-path="src/components/questions/FileUploadSection.tsx">{progress}%</span>
                    </div>
                    <Progress value={progress} className="w-full" data-id="grhcnbdvh" data-path="src/components/questions/FileUploadSection.tsx" />
                  </div>
              }
              </CardContent>
            </Card>
          }

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={handleFileSelect}
            className="hidden"
            disabled={isProcessing} data-id="uyy794o07" data-path="src/components/questions/FileUploadSection.tsx" />

        </div>

        {/* Process Button */}
        <Button
          onClick={handleProcessFile}
          disabled={!selectedFile || isProcessing || !localSubject.trim()}
          className="w-full" data-id="n8xh6bdb1" data-path="src/components/questions/FileUploadSection.tsx">

          {isProcessing ?
          <>
              <AlertCircle className="h-4 w-4 mr-2 animate-spin" data-id="rvpppgazh" data-path="src/components/questions/FileUploadSection.tsx" />
              Processing File...
            </> :

          <>
              <CheckCircle className="h-4 w-4 mr-2" data-id="dqtviblyv" data-path="src/components/questions/FileUploadSection.tsx" />
              Extract Questions
            </>
          }
        </Button>

        {/* Help Text */}
        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg" data-id="m2etuzdxn" data-path="src/components/questions/FileUploadSection.tsx">
          <strong data-id="mw720wwns" data-path="src/components/questions/FileUploadSection.tsx">Tips for better results:</strong>
          <ul className="mt-1 space-y-1 list-disc list-inside" data-id="1n0o3zv92" data-path="src/components/questions/FileUploadSection.tsx">
            <li data-id="u6kgrn4aj" data-path="src/components/questions/FileUploadSection.tsx">Ensure text is clear and readable</li>
            <li data-id="6tlnsdy8k" data-path="src/components/questions/FileUploadSection.tsx">PDF files generally produce better results than images</li>
            <li data-id="jh3wpqm2n" data-path="src/components/questions/FileUploadSection.tsx">Questions should be properly formatted in the source material</li>
            <li data-id="pho2ztmyp" data-path="src/components/questions/FileUploadSection.tsx">AI will generate various question types based on the content</li>
          </ul>
        </div>
      </CardContent>
    </Card>);

};

export default FileUploadSection;